import {create} from 'zustand'
import type {ReactNode} from 'react'

export interface ConfirmDialogOptions {
  title?: string
  message: ReactNode
  confirmText?: string
  cancelText?: string
  variant?: 'primary' | 'danger' | 'warning' | 'success'
  icon?: string
}

interface ConfirmDialogState extends ConfirmDialogOptions {
  isOpen: boolean
  resolve: ((value: boolean) => void) | null
  open: (options: ConfirmDialogOptions, resolve: (value: boolean) => void) => void
  close: () => void
}

export const useConfirmDialogStore = create<ConfirmDialogState>((set) => ({
  isOpen: false,
  title: undefined,
  message: '',
  confirmText: undefined,
  cancelText: undefined,
  variant: 'primary',
  icon: undefined,
  resolve: null,
  open: (options, resolve) => set({...options, isOpen: true, resolve}),
  close: () => set({isOpen: false, resolve: null}),
}))

/**
 * Imperative confirmation dialog — same idea as `toast.success(...)`: call it from
 * anywhere, no modal open/close state to wire up at the call site. Requires
 * <ConfirmDialogHost /> to be mounted once at the app root (see App.tsx).
 */
export const confirmDialog = (options: ConfirmDialogOptions): Promise<boolean> =>
  new Promise((resolve) => {
    useConfirmDialogStore.getState().open(options, resolve)
  })

/** Shortcut for the common "delete this?" case — danger-styled by default. */
export const confirmDelete = (
  message: ReactNode,
  options?: Partial<ConfirmDialogOptions>
): Promise<boolean> =>
  confirmDialog({
    title: 'Delete',
    confirmText: 'Delete',
    variant: 'danger',
    ...options,
    message,
  })
